export const PILL_BORDER_DEFAULTS = {
  strokeWidth: 0.75,
  dash: "30 2 15 7",
  dashOffset: -11,
};

// All paths below are built in a fixed 0-100 (percentage) coordinate space,
// paired with viewBox="0 0 100 100" + preserveAspectRatio="none" on the
// consuming <svg>. That way the path never has to match an exact pixel
// measurement of its box — the browser always stretches it to fill whatever
// the box's current actual size is, in both axes independently, with no
// risk of aspect-ratio-preserving letterboxing leaving a gap.
export const buildPillPath = (
  W,
  H,
  strokeWidth = PILL_BORDER_DEFAULTS.strokeWidth
) => {
  const sw = strokeWidth;
  const R = H / 2;
  const p = sw / 2;
  const iw = W - p * 2;
  const ih = H - p * 2;
  // Cap the radius at half the available width — otherwise, for boxes taller
  // relative to their width than a typical pill (e.g. a short link inside
  // large headline text), the height-derived radius overshoots the middle,
  // pushing the straight run backwards and collapsing the shape.
  const r = Math.min(R - p, iw / 2);
  const ox = p;
  const oy = p;

  const x = (v) => (v / W) * 100;
  const y = (v) => (v / H) * 100;
  const rx = (v) => (v / W) * 100;
  const ry = (v) => (v / H) * 100;

  // Clockwise: top-left → top-right → right cap ↓ → bottom-right → bottom-left → left cap ↑
  return `M ${x(ox + r)},${y(oy)} L ${x(ox + iw - r)},${y(oy)} A ${rx(r)},${ry(
    r
  )} 0 1,1 ${x(ox + iw - r)},${y(oy + ih)} L ${x(ox + r)},${y(oy + ih)} A ${rx(
    r
  )},${ry(r)} 0 1,1 ${x(ox + r)},${y(oy)} Z`;
};

// Builds a sharp rectangular SVG path inset by the stroke width.
export const buildRectPath = (
  W,
  H,
  strokeWidth = PILL_BORDER_DEFAULTS.strokeWidth
) => {
  const p = strokeWidth / 2;
  const ox = p;
  const oy = p;
  const iw = W - p * 2;
  const ih = H - p * 2;

  const x = (v) => (v / W) * 100;
  const y = (v) => (v / H) * 100;

  return `M ${x(ox)},${y(oy)} L ${x(ox + iw)},${y(oy)} L ${x(ox + iw)},${y(
    oy + ih
  )} L ${x(ox)},${y(oy + ih)} Z`;
};

export const buildPillSvgString = (
  W,
  H,
  {
    strokeWidth = PILL_BORDER_DEFAULTS.strokeWidth,
    dash = PILL_BORDER_DEFAULTS.dash,
    dashOffset = PILL_BORDER_DEFAULTS.dashOffset,
  } = {}
) => {
  const d = buildPillPath(W, H, strokeWidth);
  const shared = `fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"`;

  return (
    `<span class="dashed-border" style="position:absolute;inset:0;pointer-events:none;z-index:0;border-radius:9999px;overflow:visible;">` +
    `<svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" style="position:absolute;inset:0;width:100%;height:100%;overflow:visible;">` +
    `<path ${shared} stroke-dasharray="${dash}" stroke-dashoffset="${dashOffset}" d="${d}" />` +
    `</svg>` +
    `</span>`
  );
};
